import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { getChainConfig, getRpcUrlForChain, getRpcUrlsForChain } from '@/chains';

export interface UseContractAdminResult {
  adminAddress: string | null;
  isLoading: boolean;
  error: string | null;
}

const OWNER_ABI = ['function owner() view returns (address)'];
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

function resolveRpcUrls(chainId: number): string[] {
  try {
    const urls = getRpcUrlsForChain(chainId);
    if (urls.length > 0) return urls;
  } catch {
    // fall through
  }
  try {
    return [getRpcUrlForChain(chainId)];
  } catch {
    return [];
  }
}

export function useContractAdmin(
  chainId: number | null,
  bridgeAddress: string,
): UseContractAdminResult {
  const [adminAddress, setAdminAddress] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAdmin = async () => {
      if (
        chainId == null ||
        !bridgeAddress ||
        bridgeAddress.toLowerCase() === ZERO_ADDRESS
      ) {
        setAdminAddress(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setAdminAddress(null);
      setError(null);

      // PoD portals have no Ownable — factory is the admin authority.
      const chainCfg = getChainConfig(chainId);
      if (chainCfg?.portalStrategy === 'pod-privacy-portal') {
        const factoryAddress = chainCfg.addresses.PrivacyPortalFactory ?? null;
        if (!cancelled) {
          setAdminAddress(factoryAddress);
          setError(factoryAddress ? null : 'PrivacyPortalFactory not configured');
          setIsLoading(false);
        }
        return;
      }

      const rpcUrls = resolveRpcUrls(chainId);
      if (rpcUrls.length === 0) {
        setError('No RPC configured for this chain');
        setIsLoading(false);
        return;
      }

      let lastError: unknown;
      for (const rpcUrl of rpcUrls) {
        const provider = new ethers.JsonRpcProvider(rpcUrl, undefined, { staticNetwork: true });
        try {
          const contract = new ethers.Contract(bridgeAddress, OWNER_ABI, provider);
          const owner: string = await contract.owner();
          if (!cancelled) {
            setAdminAddress(owner);
            setError(null);
          }
          return;
        } catch (err) {
          lastError = err;
        } finally {
          provider.destroy();
        }
      }

      if (!cancelled) {
        setAdminAddress(null);
        setError(
          lastError instanceof Error ? lastError.message : 'Failed to fetch admin address',
        );
      }
    };

    fetchAdmin().finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [chainId, bridgeAddress]);

  return { adminAddress, isLoading, error };
}
